import { NavLink, useLocation } from 'react-router-dom';
import {
  CalendarCheck, Globe, Inbox, LayoutDashboard, Mail, MessageCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The Website Engagement frame: one heading and one band of tabs.
 *
 * The four inboxes underneath it are separate record types with separate
 * lifecycles — a chat session, a contact-form message, a mailing-list opt-in
 * and an event registration — so they are tabs rather than filters on one
 * list. What they share is where they come from, and this band is what says
 * so.
 *
 * Two of the tabs point at their original URLs (/admin/website-messages and
 * /admin/website-signups) rather than at addresses under this area. Every link
 * already sent, every notification sitting in someone's inbox and every
 * bookmark keeps working, and the band is what makes them read as part of one
 * section anyway.
 */

const TABS = [
  { label: 'Overview', path: '/admin/website-engagement/overview', icon: LayoutDashboard },
  { label: 'Online Chat Messages', path: '/admin/website-messages', icon: MessageCircle },
  {
    label: 'Contact Form Inquiries',
    path: '/admin/website-engagement/contact-inquiries',
    icon: Inbox,
  },
  { label: 'Newsletter Subscribers', path: '/admin/website-engagement/newsletter', icon: Mail },
  { label: 'Event Signups', path: '/admin/website-signups', icon: CalendarCheck },
];

/**
 * Whether a tab owns the current page, including its detail routes: an inquiry
 * at /contact-inquiries/abc keeps "Contact Form Inquiries" lit, the way opening a
 * record anywhere else in the portal does.
 */
export function isEngagementTabActive(path, pathname) {
  if (!path || !pathname) return false;
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function WebsiteEngagementHeader({ title, description, actions }) {
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div className="min-w-0">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-[#D4AF37]">
          <Globe className="size-3.5" />
          Website Engagement
        </p>
        <h1
          className="mt-1 text-2xl font-semibold text-[#062B1A] sm:text-3xl"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {title}
        </h1>
        {description && (
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#062B1A]/60">{description}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export default function WebsiteEngagementShell({ title, description, actions, children }) {
  const { pathname } = useLocation();

  return (
    <div className="animate-fade-in space-y-5">
      <WebsiteEngagementHeader title={title} description={description} actions={actions} />
      {/* Wraps rather than scrolls on a phone. A sideways-scrolling tab strip
          hides its own tabs: the ones off-screen give no hint they exist, and
          this band is the only signpost that these five inboxes are one area. */}
      <nav
        aria-label="Website Engagement"
        className="rounded-xl border border-[#062B1A]/8 bg-white p-2 shadow-sm"
      >
        <div className="flex flex-wrap gap-1">
          {TABS.map((tab) => {
            const active = isEngagementTabActive(tab.path, pathname);
            return (
              <NavLink
                key={tab.path}
                to={tab.path}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'inline-flex items-center rounded-lg px-2.5 py-2 text-[13px] font-medium transition-colors sm:px-3 sm:text-sm',
                  active
                    ? 'bg-[#062B1A] text-white'
                    : 'text-[#062B1A]/60 hover:bg-[#062B1A]/5 hover:text-[#062B1A]',
                )}
              >
                <tab.icon className="mr-2 size-4" />
                {tab.label}
              </NavLink>
            );
          })}
        </div>
      </nav>
      {children}
    </div>
  );
}

export { TABS as WEBSITE_ENGAGEMENT_TABS };
